// Copyright (c) Docugami, Inc. All rights reserved.

import { useState, useEffect } from 'react';
import { MessageBar, MessageBarType } from "@fluentui/react";
import { KeyboardAccelerators } from '../utils/KeyboardAccelerators';
import { browser } from '../../..';
import { SettingsPanel } from '../../settings/presentation/SettingsPanel';
import { DgHeader } from './Header';
import CookieConsent from "react-cookie-consent";
import "../styles/shell.scss";
import { useOrganizationFeatures } from 'hooks/OrganizationFeatures';
import { useSessionTimeout } from 'networking/SessionTimeout';
import { GlobalNavigationBar } from 'components/shell/presentation/GlobalNavigationBar';
import { useDgAuth } from 'hooks/DgAuthentication';
import { useOrgStatus } from 'hooks/OrgStatus';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import styled from 'styled-components';
import { resetNetworkErrors } from 'store/errorSlice';
import { useActivityTracking } from 'hooks/activity';

const NetworkErrorContainer = styled.div`
    position: relative;
    z-index: 999;
    display: flex;
`

export interface AppShellProps {
    children?: JSX.Element | JSX.Element[];
}

export const AppShell = (props: AppShellProps): JSX.Element => {
    const [browserWarningDismissed, setBrowserWarningDismissed] = useState(false);

    const { currentOrgInitializing, needToCreateAnOrg } = useOrgStatus();
    const { isLoading, isAuthenticated, hasOrgClaims } = useDgAuth();
    const SessionTimeoutModal = useSessionTimeout()
    const supportedBrowsers = ['chrome', 'edge-chromium'];
    const officeMode = (window as any)._OfficeMode;
    useOrganizationFeatures();
    useActivityTracking();

    const [showNetworkErrorMessage, setShowNetworkErrorMessage] = useState(false);
    const networkErrorCount = useAppSelector(state => state.errors.networkErrorCount);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (networkErrorCount >= 3) {
            setShowNetworkErrorMessage(true);
        }
    }, [networkErrorCount])

    useEffect(() => {
        if (!showNetworkErrorMessage) {
            dispatch(resetNetworkErrors());
        }
    }, [showNetworkErrorMessage, dispatch])

    useEffect(() => {
        KeyboardAccelerators.init();
    }, [])

    function browserWarning(): JSX.Element | null {
        if (isLoading || !isAuthenticated) {
            return null;
        }

        if (!officeMode && !supportedBrowsers.find(x => x === browser.name) && !browserWarningDismissed) {
            return <MessageBar
                onDismiss={() => setBrowserWarningDismissed(true)}
                dismissButtonAriaLabel="Dismiss"
                messageBarType={MessageBarType.warning}>Docugami currently recommends using either Google Chrome or the new Microsoft Edge for best results.</MessageBar>
        }

        return null;
    }

    function getNetworkErrorMessage(): JSX.Element | null {
        if (showNetworkErrorMessage) {
            return (
                <NetworkErrorContainer>
                    <MessageBar
                        styles={{ root: { position: 'absolute', top: 0, left: 0 } }}
                        key={'network-error'}
                        messageBarType={MessageBarType.warning}
                        onDismiss={() => setShowNetworkErrorMessage(false)}
                        dismissButtonAriaLabel="Dismiss">
                        Docugami is having trouble connecting to the internet. Please check your connection and try again after refreshing the page.
                    </MessageBar>
                </NetworkErrorContainer>
            )
        }

        return null;
    }

    const showHeader = !isLoading && isAuthenticated && !officeMode && hasOrgClaims && !currentOrgInitializing && !needToCreateAnOrg;

    return (
        <div className="shellFullWindow">
            {showHeader ? <DgHeader /> : <></>}
            {getNetworkErrorMessage()}
            {SessionTimeoutModal}
            {browserWarning()}
            <div className="shellAppSpace">
                {showHeader && <GlobalNavigationBar />}
                {props.children}
            </div>
            <SettingsPanel />
            <div className="cookie-confirmation-display">
                <CookieConsent
                    location="bottom"
                    buttonText="Accept"
                    style={{ background: "#2B373B", justifyContent: "flex-start" }}
                    contentClasses="cookieContent">
                    This website stores cookies on your computer. These cookies are used to collect information about how you interact with our website and allow us to remember you.
                    We use this information in order to improve and customize your browsing experience and for analytics and metrics about our visitors both on this website and other media.
                    To find out more about the cookies we use, see our Privacy Policy.
                </CookieConsent>
            </div>
        </div>
    )
}
